import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import './Layout.css';

const SideNav = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, logout, userData } = useAuth();
    const { showToast } = useToast();

    // Check if user is super admin
    const isSuperAdmin = userData?.isSuperAdmin === true;

    const navigationItems = [
        { id: 'dashboard', icon: 'fa-home', label: 'Dashboard', path: '/dashboard', requiresAuth: true },
        { id: 'members', icon: 'fa-users', label: 'Members', path: '/members', requiresAuth: true },
        { id: 'my-family', icon: 'fa-home', label: 'My Family', path: '/family', requiresAuth: true },
        { id: 'finance', icon: 'fa-money-bill-wave', label: 'Wallet', path: '/my-wallet', requiresAuth: true },
        { id: 'missions', icon: 'fa-hands-helping', label: 'Missions', path: '/missions', requiresAuth: true },
        { id: 'events', icon: 'fa-calendar-alt', label: 'Events', path: '/events', requiresAuth: true },
        { id: 'hymns', icon: 'fa-music', label: 'Dovesnal', path: '/hymns', requiresAuth: true },
        { id: 'welfare', icon: 'fa-hand-holding-heart', label: 'Welfare', path: '/welfare', requiresAuth: true },
        { id: 'documents', icon: 'fa-file-alt', label: 'Documents', path: '/documents', requiresAuth: true },
        { id: 'uniforms', icon: 'fa-user-tie', label: 'Uniforms', path: '/uniforms', requiresAuth: true },
        { id: 'council', icon: 'fa-gavel', label: 'Council', path: '/council', requiresAuth: true },
        { id: 'music', icon: 'fa-music', label: 'Music', path: '/music', requiresAuth: true },
        { id: 'prayers', icon: 'fa-praying-hands', label: 'Prayers', path: '/prayers', requiresAuth: true },
        { id: 'gallery', icon: 'fa-images', label: 'Gallery', path: '/gallery', requiresAuth: true },
        { id: 'projects', icon: 'fa-project-diagram', label: 'Projects', path: '/projects', requiresAuth: true },
        { id: 'register', icon: 'fa-user-plus', label: 'Register', path: '/register', requiresAuth: true },
        { id: 'settings', icon: 'fa-cog', label: 'Settings', path: '/settings', requiresAuth: true },
        { id: 'help-me', icon: 'fa-hand-holding-heart', label: 'Help me', path: '/help-me', requiresAuth: true },
        { id: 'payment', icon: 'fa-money-bill-wave', label: 'Contributions', path: '/payment', requiresAuth: true },
        // Add Communication link
        { id: 'communication', icon: 'fa-bullhorn', label: 'Communication', path: '/communication', requiresAuth: true },
        // Add SMS Communication link
        { id: 'smscommunication', icon: 'fa-sms', label: 'SMS', path: '/smscommunication', requiresAuth: true },
    ];

    const adminNavigationItems = [
        { id: 'addYear', icon: 'fa-calendar-plus', label: 'Add New Year', action: 'modal', modal: 'addYearModal' },
        { id: 'sendNotification', icon: 'fa-bell', label: 'Send Notification', action: 'modal', modal: 'notificationModal' },
        { id: 'manageAdmins', icon: 'fa-user-shield', label: 'Manage Admins', path: '/super-admin' },
        { id: 'accounts', icon: 'fa-wallet', label: 'Wallet Admin', path: '/admin-wallet', requiresAuth: true },
        { id: 'finance', icon: 'fa-money-bill-wave', label: 'Finance Admin', path: '/finance', requiresAuth: true },
        { id: 'letter', icon: 'fa-file-alt', label: 'Letter', path: '/letter' },
        { id: 'reports', icon: 'fa-chart-bar', label: 'Reports', path: '/reports' },
        { id: 'mission-toolkit', icon: 'fa-toolbox', label: 'Mission Toolkit', path: '/mission-toolkit', requiresAuth: true },
        { id: 'family-allocation', icon: 'fa-home', label: 'Family Allocation', path: '/family-allocation', requiresAuth: true },
    ];

    // Super Admin specific navigation items
    const superAdminItems = [
        { id: 'super-admin', icon: 'fa-crown', label: 'Super Admin Panel', path: '/super-admin', requiresAuth: true },
    ];

    const handleNavigation = (item) => {
        if (item.action === 'modal') {
            window.dispatchEvent(new CustomEvent('openModal', { detail: { modalId: item.modal } }));
        } else if (item.action === 'toast') {
            showToast(item.message, 'info');
        } else {
            navigate(item.path);
        }
        onClose();
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
            showToast('Logged out successfully', 'success');
        } catch (error) {
            showToast('Error logging out', 'error');
        }
        onClose();
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    return (
        <>
            <div className={`side-nav ${isOpen ? 'open' : ''}`}>
                {/* User Navigation Items */}
                {navigationItems.map(item => (
                    <div
                        key={item.id}
                        className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                        onClick={() => handleNavigation(item)}
                    >
                        <i className={`fas ${item.icon}`}></i>
                        <span>{item.label}</span>
                    </div>
                ))}

                {/* Admin Section */}
                {isAdmin && (
                    <div className="nav-admin-section">
                        <div className="nav-admin-header">Administration</div>
                        {adminNavigationItems.map(item => (
                            <div
                                key={item.id}
                                className={`nav-item admin ${isActive(item.path) ? 'active' : ''}`}
                                onClick={() => handleNavigation(item)}
                            >
                                <i className={`fas ${item.icon}`}></i>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Super Admin Section */}
                {isSuperAdmin && (
                    <div className="nav-admin-section">
                        <div className="nav-admin-header" style={{ color: '#c62828' }}>
                            <i className="fas fa-crown"></i> Super Admin
                        </div>
                        {superAdminItems.map(item => (
                            <div
                                key={item.id}
                                className={`nav-item super-admin ${isActive(item.path) ? 'active' : ''}`}
                                onClick={() => handleNavigation(item)}
                            >
                                <i className={`fas ${item.icon}`}></i>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Logout */}
                <div className="nav-item logout" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                </div>
            </div>
            <div className={`overlay ${isOpen ? 'show' : ''}`} onClick={onClose}></div>
        </>
    );
};

export default SideNav;
